/* Original name @uswitch/trustyle.drop-down */

import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useThemeUI } from 'theme-ui'
import { TruCaret } from '@uswitch/spark-icons/tru'
import { FrozenInput } from '../frozen-input'
import { path } from '@uswitch/ev-utils/obj'

const prependDataProps = dataProps =>
  Object.keys(dataProps).reduce(
    (props, key) => ({
      ...props,
      [`data-${key}`]: dataProps[key],
    }),
    {},
  )

const DropDown = forwardRef(
  (
    {
      dataProps = {},
      disabled,
      freezable,
      hasError = false,
      onBlur = () => {},
      onChange,
      onFocus = () => {},
      onClick = () => {},
      name,
      options,
      placeholder,
      value,
      overlay,
      className,
    },
    ref,
  ) => {
    const { theme } = useThemeUI()
    const [hasFocus, setHasFocus] = useState(false)
    const option = options.find(_ => _.value === value)
    const frozenText = option && option.text
    const inputRef = useRef(null)

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current && inputRef.current.focus(),
      scrollIntoView: (...args) => inputRef.current && inputRef.current.scrollIntoView(...args),
    }))

    const variant = hasError
      ? 'elements.drop-down.select.variants.error'
      : hasFocus
      ? 'elements.drop-down.select.variants.focus'
      : 'elements.drop-down.select.base'

    const iconColor =
      path(`${variant}.iconColor`.split('.'), theme) ||
      path('elements.drop-down.select.base.iconColor'.split('.'), theme) ||
      path(`${variant}.borderColor`.split('.'), theme) ||
      path('elements.drop-down.select.base.borderColor'.split('.'), theme)

    return (
      <FrozenInput text={frozenText} freezable={freezable} inputRef={inputRef}>
        <div
          sx={{
            position: 'relative',
            variant: 'elements.drop-down.container',
          }}
        >
          <If condition={overlay}>
            <div
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                paddingX: 16,
                variant,
              }}
              className={className}
            >
              {overlay}
            </div>
          </If>
          <select
            ref={inputRef}
            onFocus={() => {
              setHasFocus(true)
              onFocus()
            }}
            onBlur={() => {
              setHasFocus(false)
              onBlur()
            }}
            onChange={e => onChange(e.currentTarget.value)}
            onClick={() => onClick()}
            sx={{
              opacity: overlay ? 0 : undefined,
              fontFamily: 'base',
              fontSize: 'base',
              lineHeight: '1.33',
              appearance: 'none',
              backgroundColor: 'white',
              boxSizing: 'border-box',
              padding: 16,
              paddingRight: 48,
              verticalAlign: 'middle',
              borderRadius: 0,
              width: '100%',
              '&::-ms-expand': {
                display: 'none',
              },
              height: '100%',
              variant:
                placeholder && value === undefined ? 'elements.drop-down.placeholder' : variant,
            }}
            id={name}
            name={name}
            className={className}
            value={!value ? '' : value}
            disabled={disabled}
            {...prependDataProps(dataProps)}
          >
            <If condition={placeholder}>
              <option value='' disabled>
                {placeholder}
              </option>
            </If>
            <For each='propOption' of={options}>
              <option key={propOption.text} value={propOption.value}>
                {propOption.text}
              </option>
            </For>
          </select>
          <If condition={!overlay}>
            <span
              sx={{
                height: 15,
                width: 15,
                pointerEvents: 'none',
                position: 'absolute',
                right: 16,
                top: 'calc(50% - 15px / 2)',
                variant: 'elements.drop-down.icon',
              }}
            >
              <TruCaret color={iconColor} sx={{ transform: 'rotate(0.5turn)' }} />
            </span>
          </If>
        </div>
      </FrozenInput>
    )
  },
)

export default DropDown
